"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  CreateShopPromotionDTO,
  UpdateShopPromotionDTO,
} from "@/modules/merchant/domain/contracts";
import {
  shopCatalogApi,
  type ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import {
  useCreateShopPromotionMutation,
  useShopBranchesListQuery,
  useShopPromotionDetailQuery,
  useUpdateShopPromotionMutation,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import type { ShopCurrency } from "@/shared/lib/currency";
import { SHOP_CURRENCIES, isShopCurrency } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormInput,
  FormRoot,
  Label,
  Loader,
  RHF,
  cn,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Banknote, ChevronLeft, Percent, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const ALL_BRANCH_VALUE = "__all__" as const;
const ALL_CURRENCY_VALUE = "__all__" as const;
const LIST_ROUTE = "/app/shops/$shopId/promotions" as const;

/** ຄ່າ input type="date" — ວັນຕາມປະຕິທິນຂອງເຄື່ອງ (local) */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** ຈາກ ISO backend → YYYY-MM-DD ສຳລັບ type="date" (ວັນທີໃນ timezone local) */
function isoToDateOnlyLocal(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(+d)) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch {
    return "";
  }
}

/** YYYY-MM-DD (local) → ISO ຕົ້ນວັນ 00:00:00.000 local */
function localYmdToStartOfDayISO(ymd: string): string | undefined {
  const m = DATE_ONLY_RE.exec(ymd.trim());
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(y, mo - 1, day, 0, 0, 0, 0);
  if (
    d.getFullYear() !== y ||
    d.getMonth() !== mo - 1 ||
    d.getDate() !== day
  ) {
    return undefined;
  }
  return d.toISOString();
}

/** YYYY-MM-DD (local) → ISO ປາຍວັນ 23:59:59.999 local */
function localYmdToEndOfDayISO(ymd: string): string | undefined {
  const m = DATE_ONLY_RE.exec(ymd.trim());
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(y, mo - 1, day, 23, 59, 59, 999);
  if (
    d.getFullYear() !== y ||
    d.getMonth() !== mo - 1 ||
    d.getDate() !== day
  ) {
    return undefined;
  }
  return d.toISOString();
}

const uuidLoose = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    s.trim(),
  );

const PromotionFormSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ໂປຣ"),
    status: z.enum(["draft", "active", "paused"]),
    priority: z.coerce.number().int().min(0, "ລຳດັບ >= 0"),
    /** ວ່າງ = ບໍ່ຈຳກັດ – ສົ່ງ null ໄປ API */
    startsAtLocal: z.string(),
    endsAtLocal: z.string(),
    branchId: z
      .string()
      .refine((v) => v === ALL_BRANCH_VALUE || uuidLoose(v), {
        message: "ສາຂາບໍ່ຖືກ",
      }),
    billCurrency: z
      .string()
      .refine(
        (v) => v === ALL_CURRENCY_VALUE || isShopCurrency(v),
        { message: "ສະກຸນບິນບໍ່ຖືກ" },
      ),
    ruleType: z.enum(["line_percent_off", "line_amount_off"]),
    percentOff: z.number().optional(),
    amountOff: z.number().optional(),
    targetSkuIdsText: z.string(),
  })
  .superRefine((data, ctx) => {
    const sTrim = data.startsAtLocal.trim();
    const eTrim = data.endsAtLocal.trim();
    if (sTrim !== "") {
      if (!DATE_ONLY_RE.test(sTrim)) {
        ctx.addIssue({
          code: "custom",
          path: ["startsAtLocal"],
          message: "ເລືອກວັນເລີ່ມໃຫ້ຖືກ",
        });
      } else if (localYmdToStartOfDayISO(data.startsAtLocal) == null) {
        ctx.addIssue({
          code: "custom",
          path: ["startsAtLocal"],
          message: "ວັນເລີ່ມບໍ່ຖືກ",
        });
      }
    }
    if (eTrim !== "") {
      if (!DATE_ONLY_RE.test(eTrim)) {
        ctx.addIssue({
          code: "custom",
          path: ["endsAtLocal"],
          message: "ເລືອກວັນສິ້ນໃຫ້ຖືກ",
        });
      } else if (localYmdToEndOfDayISO(data.endsAtLocal) == null) {
        ctx.addIssue({
          code: "custom",
          path: ["endsAtLocal"],
          message: "ວັນສິ້ນບໍ່ຖືກ",
        });
      }
    }
    if (
      sTrim !== "" &&
      eTrim !== "" &&
      localYmdToStartOfDayISO(data.startsAtLocal) != null &&
      localYmdToEndOfDayISO(data.endsAtLocal) != null &&
      eTrim < sTrim
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["endsAtLocal"],
        message: "ວັນສິ້ນຕ້ອງຫຼັງຫຼືເທົ່າກັບວັນເລີ່ມ",
      });
    }
    if (data.ruleType === "line_percent_off") {
      if (
        data.percentOff == null ||
        !Number.isFinite(data.percentOff) ||
        data.percentOff <= 0
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["percentOff"],
          message: "ໃສ່ % ທີ່ > 0",
        });
      }
    }
    if (data.ruleType === "line_amount_off") {
      if (
        data.amountOff == null ||
        !Number.isFinite(data.amountOff) ||
        data.amountOff <= 0
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["amountOff"],
          message: "ໃສ່ຈຳນວນລົບ",
        });
      }
    }
  });

type PromotionFormValues = z.infer<typeof PromotionFormSchema>;

function defaultCreatePromotionValues(): PromotionFormValues {
  const n = new Date();
  const w = new Date(n.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    name: "",
    status: "draft",
    priority: 0,
    startsAtLocal: isoToDateOnlyLocal(n.toISOString()),
    endsAtLocal: isoToDateOnlyLocal(w.toISOString()),
    branchId: ALL_BRANCH_VALUE,
    billCurrency: ALL_CURRENCY_VALUE,
    ruleType: "line_percent_off",
    percentOff: 10,
    amountOff: undefined,
    targetSkuIdsText: "",
  };
}

function parseSkuIds(text: string): string[] {
  const re =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const parts = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const out: string[] = [];
  for (const p of parts) {
    if (re.test(p)) out.push(p);
  }
  return out;
}

function addSkuIdToText(text: string, id: string): string {
  const ids = parseSkuIds(text);
  if (ids.includes(id)) return text;
  return [...ids, id].join("\n");
}

function removeSkuIdFromText(text: string, id: string): string {
  return parseSkuIds(text).filter((x) => x !== id).join("\n");
}

function PromotionSkuChip({
  shopId,
  skuId,
  onRemove,
}: {
  shopId: string;
  skuId: string;
  onRemove: () => void;
}) {
  const q = useQuery({
    queryKey: ["shops", shopId, "promotion-form", "sku-label", skuId],
    queryFn: () => shopCatalogApi.getSellableSku(shopId, skuId),
    enabled: !!shopId && !!skuId,
    staleTime: 60_000,
  });
  const label = q.data
    ? `${q.data.productName} · ${q.data.shopSku}`
    : q.isError
      ? skuId
      : "…";

  return (
    <div
      className="flex max-w-full items-center gap-1 rounded-md border border-border bg-muted/50 py-1 pr-1 pl-2 text-sm"
      title={skuId}
    >
      <span className="min-w-0 truncate text-foreground">{label}</span>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={onRemove}
        aria-label="ລຶບອອກ"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

function PromotionTargetSkusField({
  shopId,
  value,
  onChange,
}: {
  shopId: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const [pickerKey, setPickerKey] = useState(0);
  const ids = useMemo(() => parseSkuIds(value), [value]);
  const idSet = useMemo(() => new Set(ids), [ids]);

  const comboboxQueryKey = useMemo(
    () => ["shops", shopId, "promotion-form", "sku-picker"] as string[],
    [shopId],
  );

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-sm">ເພີ່ມສິນຄ້າ (ຄົ້ນຫາຈາກລາຍການຂາຍໄດ້)</Label>
        <InfiniteCombobox<ShopSellableCatalogRow>
          key={pickerKey}
          queryKey={comboboxQueryKey}
          queryFn={async ({ search: q, pageParam }) => {
            const limit = 20;
            const page = pageParam ?? 1;
            const offset = (page - 1) * limit;
            const res = await shopCatalogApi.listSellable(shopId, {
              offset,
              limit,
              q: q.trim() || undefined,
              barcode: undefined,
            });
            const next =
              offset + res.data.length < res.meta.total ? page + 1 : null;
            return { items: res.data, nextPage: next };
          }}
          preloadQueryFn={(id) => shopCatalogApi.getSellableSku(shopId, id)}
          getLabel={(item) =>
            `${item.productName} · ${item.shopSku} (${item.currency})`
          }
          getValue={(item) => item.id}
          getDisabled={(item) => idSet.has(item.id)}
          value=""
          onValueChange={(id) => {
            if (!id) return;
            onChange(addSkuIdToText(value, id));
            setPickerKey((k) => k + 1);
          }}
          placeholder="ຄົ້ນຫາຊື່, SKU, barcode..."
          className="h-10 w-full"
        />
        <p className="text-muted-foreground text-xs">
          ເລືອກສິນຄ້າໜຶ່ງຄັ້ງ = ເພີ່ມໜຶ່ງເປົ້າໝາຍ. ລາຍການທີ່ເລືອກແລ້ວຈະຖືກປິດບໍ່ໃຫ້ເລືອກຊ້ຳ.
        </p>
      </div>

      {ids.length > 0 ? (
        <div className="space-y-2">
          <Label className="text-sm">
            ເປົ້າໝາຍທີ່ເລືອກ ({ids.length})
          </Label>
          <ul className="flex flex-col gap-2">
            {ids.map((id) => (
              <li key={id}>
                <PromotionSkuChip
                  shopId={shopId}
                  skuId={id}
                  onRemove={() => onChange(removeSkuIdFromText(value, id))}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <details className="rounded-md border border-dashed p-3">
        <summary className="cursor-pointer text-muted-foreground text-sm">
          ວາງ / ແກ້ UUID ເອງ (ຫຼາຍບັນທຶກພ້ອມກັນ)
        </summary>
        <p className="mt-2 mb-2 text-muted-foreground text-xs">
          ໃຫ້ຄ່າູນດ້ວຍຂຶ້ນບັນທາດ, ວັກຍຸດ, ຫຼື ; — ລະບົບຈະດຶງສະເພາະ uuid ທີ່ຖືກ.
        </p>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
          placeholder="uuid ... uuid ..."
        />
      </details>
    </div>
  );
}

function toCreateApiBody(vals: PromotionFormValues): CreateShopPromotionDTO {
  const sTrim = vals.startsAtLocal.trim();
  const eTrim = vals.endsAtLocal.trim();
  let startsAt: string | null = null;
  let endsAt: string | null = null;
  if (sTrim !== "") {
    const iso = localYmdToStartOfDayISO(vals.startsAtLocal);
    if (iso == null) throw new Error("ວັນເລີ່ມບໍ່ຖືກ");
    startsAt = iso;
  }
  if (eTrim !== "") {
    const iso = localYmdToEndOfDayISO(vals.endsAtLocal);
    if (iso == null) throw new Error("ວັນສິ້ນບໍ່ຖືກ");
    endsAt = iso;
  }
  return {
    name: vals.name.trim(),
    status: vals.status,
    priority: vals.priority,
    startsAt,
    endsAt,
    branchId:
      vals.branchId === ALL_BRANCH_VALUE ? null : vals.branchId,
    billCurrency:
      vals.billCurrency === ALL_CURRENCY_VALUE
        ? null
        : (vals.billCurrency as ShopCurrency),
    ruleType: vals.ruleType,
    percentOff:
      vals.ruleType === "line_percent_off" ? (vals.percentOff ?? null) : null,
    amountOff:
      vals.ruleType === "line_amount_off" ? (vals.amountOff ?? null) : null,
    targetSkuIds: parseSkuIds(vals.targetSkuIdsText),
  };
}

function toUpdateApiBody(vals: PromotionFormValues): UpdateShopPromotionDTO {
  return toCreateApiBody(vals) as UpdateShopPromotionDTO;
}

function mapRowToForm(row: {
  name: string;
  status: PromotionFormValues["status"];
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  branchId: string | null;
  billCurrency: string | null;
  ruleType: PromotionFormValues["ruleType"];
  percentOff: number | null;
  amountOff: number | null;
  targetSkuIds: string[];
}): PromotionFormValues {
  return {
    name: row.name,
    status: row.status,
    priority: row.priority,
    startsAtLocal:
      row.startsAt != null && row.startsAt !== ""
        ? isoToDateOnlyLocal(row.startsAt)
        : "",
    endsAtLocal:
      row.endsAt != null && row.endsAt !== ""
        ? isoToDateOnlyLocal(row.endsAt)
        : "",
    branchId: row.branchId ?? ALL_BRANCH_VALUE,
    billCurrency:
      row.billCurrency && isShopCurrency(row.billCurrency)
        ? row.billCurrency
        : ALL_CURRENCY_VALUE,
    ruleType: row.ruleType,
    percentOff: row.percentOff ?? 10,
    amountOff: row.amountOff ?? undefined,
    targetSkuIdsText: row.targetSkuIds.join("\n"),
  };
}

type ShopPromotionFormFieldsProps = {
  shopId: string;
  form: UseFormReturn<PromotionFormValues, unknown, PromotionFormValues>;
  branchSelectOptions: { value: string; label: string }[];
  billCurrencySelectOptions: { value: string; label: string }[];
};

function ShopPromotionFormFields({
  shopId,
  form,
  branchSelectOptions,
  billCurrencySelectOptions,
}: ShopPromotionFormFieldsProps) {
  const fe = form.formState.errors;
  const ruleType = form.watch("ruleType");

  return (
    <>
      <FormInput name="name" label="ຊື່ໂປຣ" requiredMark />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-sm">ສະຖານະ</Label>
          <RHF.Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  { value: "draft", label: "ຮ່າງ" },
                  { value: "active", label: "ໃຊ້ແລ້ວ" },
                  { value: "paused", label: "ຢຸດຊົ່ວຄາວ" },
                ]}
                placeholder="ເລືອກສະຖານະ"
                className="w-full"
              />
            )}
          />
        </div>
        <FormInput
          name="priority"
          label="ລຳດັບຄວາມສຳຄັນ"
          type="number"
          inputMode="numeric"
        />
      </div>
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs">
          ວັນເລີ່ມ / ວັນສິ້ນ — ວ່າງໄດ້: ວ່າງ = ບໍ່ຈຳກັດຕົ້ນ ຫຼື ປາຍ (ໂປຣ active ຍັງໃຊ້ໄດ້ຕາມທີ່ຕັ້ງ)
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm">ວັນເລີ່ມ (ບໍ່ບັງຄັບ)</Label>
            <RHF.Controller
              name="startsAtLocal"
              control={form.control}
              render={({ field }) => (
                <input
                  type="date"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  {...field}
                />
              )}
            />
            {fe.startsAtLocal?.message ? (
              <p className="text-destructive text-sm">{fe.startsAtLocal.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label className="text-sm">ວັນສິ້ນ (ບໍ່ບັງຄັບ)</Label>
            <RHF.Controller
              name="endsAtLocal"
              control={form.control}
              render={({ field }) => (
                <input
                  type="date"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  {...field}
                />
              )}
            />
            {fe.endsAtLocal?.message ? (
              <p className="text-destructive text-sm">{fe.endsAtLocal.message}</p>
            ) : null}
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-sm">ສາຂາ (ວ່າງ = ທຸກສາຂາ)</Label>
          <RHF.Controller
            name="branchId"
            control={form.control}
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onValueChange={field.onChange}
                options={branchSelectOptions}
                placeholder="ເລືອກສາຂາ"
                className="w-full"
              />
            )}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">ສະກຸນເງິນບິນ (ວ່າງ = ທຸກສະກຸນ)</Label>
          <RHF.Controller
            name="billCurrency"
            control={form.control}
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onValueChange={field.onChange}
                options={billCurrencySelectOptions}
                placeholder="ສະກຸນບິນ (ທຸກ)"
                className="w-full"
              />
            )}
          />
        </div>
      </div>
      <div className="space-y-3">
        <Label className="text-sm">ປະເພດກົດ</Label>
        <RHF.Controller
          name="ruleType"
          control={form.control}
          render={({ field }) => (
            <div
              className="grid gap-3 sm:grid-cols-2"
              role="radiogroup"
              aria-label="ປະເພດກົດ"
            >
              <button
                type="button"
                role="radio"
                aria-checked={field.value === "line_percent_off"}
                onClick={() => field.onChange("line_percent_off")}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                  field.value === "line_percent_off"
                    ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <div
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-md",
                    field.value === "line_percent_off"
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <Percent className="size-5" strokeWidth={2.25} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-foreground text-sm sm:text-base">
                    ລົບ % ຕໍ່ລາຍການ
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    ລົດລາຄາເປັນ % ຂອງມູນຄ່າລາຍການທີ່ກົດມາທວນ
                  </p>
                </div>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={field.value === "line_amount_off"}
                onClick={() => field.onChange("line_amount_off")}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                  field.value === "line_amount_off"
                    ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <div
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-md",
                    field.value === "line_amount_off"
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <Banknote className="size-5" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-foreground text-sm sm:text-base">
                    ລົບຈຳນວນ (ສະກຸນບິນ) ຕໍ່ລາຍການ
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    ລົດລາຄາເປັນຈຳນວນເງິນໃນສະກຸນຂອງບິນ
                  </p>
                </div>
              </button>
            </div>
          )}
        />
        {fe.ruleType?.message ? (
          <p className="text-destructive text-sm">{fe.ruleType.message}</p>
        ) : null}
      </div>
      {ruleType === "line_percent_off" ? (
        <RHF.Controller
          name="percentOff"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-1">
              <Label htmlFor="promo-pct" className="text-sm">ລົບ (%)</Label>
              <input
                id="promo-pct"
                type="number"
                min={0.01}
                max={100}
                step="0.01"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === ""
                      ? undefined
                      : Number(e.target.value),
                  )
                }
              />
              {fe.percentOff?.message ? (
                <p className="text-destructive text-sm">
                  {fe.percentOff.message}
                </p>
              ) : null}
            </div>
          )}
        />
      ) : (
        <RHF.Controller
          name="amountOff"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-1">
              <Label htmlFor="promo-amt" className="text-sm">
                ລົບຈຳນວນ (ສະກຸນບິນ)
              </Label>
              <input
                id="promo-amt"
                type="number"
                min={0.01}
                step="0.01"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === ""
                      ? undefined
                      : Number(e.target.value),
                  )
                }
              />
              {fe.amountOff?.message ? (
                <p className="text-destructive text-sm">
                  {fe.amountOff.message}
                </p>
              ) : null}
            </div>
          )}
        />
      )}
      <div className="space-y-2">
        <Label className="text-sm">ເປົ້າໝາຍ SKU (ສິນຄ້າຂາຍໄດ້ໃນຮ້ານ)</Label>
        <RHF.Controller
          name="targetSkuIdsText"
          control={form.control}
          render={({ field }) => (
            <PromotionTargetSkusField
              shopId={shopId}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </>
  );
}

export function ShopPromotionFormPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const promotionId = params?.promotionId as string | undefined;
  const isEdit = Boolean(promotionId);
  const nav = useNavigate();

  const createMut = useCreateShopPromotionMutation(shopId);
  const updateMut = useUpdateShopPromotionMutation(shopId);
  const branchesQ = useShopBranchesListQuery(shopId);
  const detailQ = useShopPromotionDetailQuery(shopId, promotionId);
  const branches = branchesQ.data ?? [];

  const form = RHF.useForm<PromotionFormValues>({
    resolver: zodResolver(
      PromotionFormSchema,
    ) as Resolver<PromotionFormValues>,
    defaultValues: defaultCreatePromotionValues(),
  });

  useEffect(() => {
    if (!shopId) return;
    if (isEdit) {
      if (detailQ.data) {
        form.reset(mapRowToForm(detailQ.data));
      }
    } else {
      form.reset(defaultCreatePromotionValues());
    }
  }, [shopId, isEdit, detailQ.data, form]);

  const branchSelectOptions = useMemo(
    () => [
      { value: ALL_BRANCH_VALUE, label: "— ທຸກສາຂາ —" },
      ...branches.map((b) => ({ value: b.id, label: b.name })),
    ],
    [branches],
  );

  const billCurrencySelectOptions = useMemo(
    () => [
      { value: ALL_CURRENCY_VALUE, label: "— ທຸກ —" },
      ...SHOP_CURRENCIES.map((c) => ({ value: c, label: c })),
    ],
    [],
  );

  const goList = () => {
    if (!shopId) return;
    nav({
      to: LIST_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: 20,
        status: "all",
        search: undefined,
      },
    });
  };

  const submitting = createMut.isPending || updateMut.isPending;
  const loadingBranches = branchesQ.isLoading;
  const loadingDetail = isEdit && detailQ.isLoading;
  const loadDetailFailed = isEdit && detailQ.isError;
  const canShowForm =
    !loadingBranches &&
    (!isEdit || (!detailQ.isLoading && !detailQ.isError && detailQ.data));

  if (!shopId) return null;

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.promotions.manage}
    >
      <Header>
        <Breadcrumb
          items={
            isEdit
              ? shopBreadcrumbs.promotionsEdit(shopId)
              : shopBreadcrumbs.promotionsNew(shopId)
          }
        />
      </Header>
      <Main>
        <div className="flex justify-between gap-4 mb-4">
         <div className="">
           
         <h1 className="font-semibold text-foreground text-lg">
            {isEdit ? "ແກ້ໄຂໂປຣ" : "ສ້າງໂປຣ"}
          </h1>
          <p className="text-muted-foreground text-sm">
            ລະບຸ shop_product_sku (uuid) ທີ່ມີໃນ catalog — ລົດລາຄາຕາມລຳດັບຄວາມສຳຄັນ
          </p>
         </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 gap-1 text-muted-foreground"
            onClick={goList}
          >
            <ChevronLeft className="size-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        {loadDetailFailed ? (
          <p className="text-destructive text-sm">ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ</p>
        ) : null}

        {loadingBranches || loadingDetail ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : canShowForm ? (
          <div className="mx-auto max-w-2xl">
            <FormRoot<PromotionFormValues>
              methods={form}
              onSubmit={async (vals) => {
                try {
                  if (isEdit && promotionId) {
                    const body = toUpdateApiBody(vals);
                    await updateMut.mutateAsync({
                      promotionId,
                      body,
                    });
                    toast.success("ບັນທຶກແລ້ວ");
                  } else {
                    await createMut.mutateAsync(toCreateApiBody(vals));
                    toast.success("ສ້າງແລ້ວ");
                  }
                  goList();
                } catch (e) {
                  const msg =
                    e instanceof Error ? e.message : "ມີຂໍ້ຜິດພາດ";
                  toast.error(msg);
                }
              }}
              className="space-y-4"
            >
              <ShopPromotionFormFields
                shopId={shopId}
                form={form}
                branchSelectOptions={branchSelectOptions}
                billCurrencySelectOptions={billCurrencySelectOptions}
              />
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goList}
                >
                  ຍົກເລີກ
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader /> : null}
                  <span className="ml-2">
                    {isEdit ? "ບັນທຶກ" : "ສ້າງໂປຣ"}
                  </span>
                </Button>
              </div>
            </FormRoot>
          </div>
        ) : isEdit && !loadDetailFailed ? (
          <p className="text-muted-foreground text-sm">ບໍ່ພົບໂປຣນີ້</p>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
